import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';

@Component({
  selector: 'app-tree',
  imports: [TreeModule, TooltipModule],
  template: `<p-tree
    [value]="nodes"
    [selectionMode]="mode"
    [(selection)]="selectedNodes"
    [propagateSelectionDown]="true"
    [propagateSelectionUp]="true"
    [lazy]="true"
    (onNodeExpand)="onNodeExpand($event)"
    (onNodeSelect)="onNodeSelect($event, true)"
    (onNodeUnselect)="onNodeSelect($event, false)"
    loadingMode="icon"
  >
    <ng-template let-node pTemplate="default">
      <span
        [pTooltip]="node.tooltip"
        tooltipPosition="right"
        [tooltipDisabled]="!node.tooltip"
      >
        {{ node.label }}
      </span>
    </ng-template>
  </p-tree>`,
  styles: [],
})
export class TreeComponent {
  private _dataSource: TreeNode[] = [];

  @Input()
  set dataSource(value: TreeNode[]) {
    this._dataSource = value || [];
    this.updateNodes();
  }

  get dataSource(): TreeNode[] {
    return this._dataSource;
  }

  nodes: TreeNode[] = [];
  selectedNodes: TreeNode[] = [];

  @Input()
  mode: 'single' | 'multiple' | 'checkbox' = 'checkbox';

  @Output()
  select = new EventEmitter<TreeNode | TreeNode[]>();

  @Output()
  unSelect = new EventEmitter<TreeNode | TreeNode[]>();

  @Output()
  expand = new EventEmitter<TreeNode>();

  private updateNodes(): void {
    if (!this._dataSource) {
      this.nodes = [];
      return;
    }

    for (const node of this._dataSource) {
      this.ensureNodeDefaults(node);
    }

    this.nodes = this._dataSource;
  }

  private ensureNodeDefaults(node: TreeNode): void {
    if (node.leaf === undefined) {
      node.leaf = node.loaded && (!node.children || node.children.length === 0);
    }

    if (node.children === undefined) {
      node.children = [];
    }

    if (node.loaded === undefined) {
      node.loaded = false;
    }
    if (node.loading === undefined) {
      node.loading = false;
    }

    for (const child of node.children) {
      this.ensureNodeDefaults(child);
    }
  }

  onNodeSelect(event: any, onSelected: boolean) {
    if (onSelected) {
      this.select.emit(
        this.mode === 'checkbox' || this.mode === 'multiple'
          ? this.selectedNodes
          : event.node,
      );
    } else {
      this.unSelect.emit(
        this.mode === 'checkbox' || this.mode === 'multiple'
          ? this.selectedNodes
          : event.node,
      );
    }
  }

  getSelectedNodes(): TreeNode[] {
    return this.selectedNodes || [];
  }

  async onNodeExpand(event: any) {
    const node = event.node;

    if (node.loaded || (node.children && node.children.length > 0)) {
      return;
    }

    node.loading = true;
    this.expand.emit(node);
  }

  async nodeLazyLoad(node: any) {
    node.loading = true;
    setTimeout(() => {
      const parent = this.getNode(node);
      node.children = parent
        ? parent.children.map((node) => ({
            ...node,
            children: [] as TreeNode[],
            leaf: node.children.length === 0,
          }))
        : [];
      node.loading = false;
    }, 500);
  }

  getNode(node: TreeNode): TreeNode {
    const findNode = (nodes: TreeNode[], value: any): TreeNode => {
      for (const n of nodes) {
        if (n.value === value) {
          return n;
        }

        if (n.children && n.children.length > 0) {
          const found = findNode(n.children, value);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    return findNode(this.dataSource, node.value);
  }
}

export class TreeNode {
  public label: string;
  public parent: TreeNode;
  public children: TreeNode[] = [];
  public value: any;
  public selected: boolean;
  public checked: boolean;
  public loaded: boolean;
  public leaf: boolean;
  public loading: boolean;
  public data: any;
  public selectable: boolean;
  public icon: string;
  public expandedIcon: string;
  public collapsedIcon: string;
  public tooltip: string;
}
